export interface GetArrayOverflowConfig {
	max?: number | undefined;
	minOverflow?: number;
}

interface OverflowData<T> {
	doesOverflow: boolean;
	head: T[];
	overflowCount: number;
	tail: T[];
}

const getSplitIndex = <T = unknown>(array: T[], max?: number, minOverflow = 0) =>
	(max || max === 0) && max + (minOverflow - 1) !== array.length ? max : array.length;

/**
 * @deprecated The configuration of this function is vague and unpredictable. Recommendation is to create a more robust utility to handle overflow splitting.
 */
export const splitOverflow = <T = unknown>(array: T[], { max, minOverflow }: GetArrayOverflowConfig): OverflowData<T> => {
	const index = getSplitIndex(array, max, minOverflow),
		head = array.slice(0, index),
		tail = array.slice(index);

	return {
		doesOverflow: !!tail.length,
		head,
		overflowCount: tail.length,
		tail,
	};
};

export const wrapOutOfBoundIndex = (index: number, arrayLength: number) => {
	if (arrayLength === 1) {
		return 0;
	}

	const indexModLength = index % arrayLength;

	return index < 0 ? arrayLength + indexModLength : indexModLength;
};

export const randomElement = <T = unknown>(array: T[]) => array[Math.floor(Math.random() * array.length)];

/**
 * Creates a function that can be applied to .sort() so an array of objects
 * can be sorted on a single object property.
 *
 * @param property string
 * @param order    string
 * @returns        function
 */
export const byProperty =
	<T>(property: keyof T, order: 'asc' | 'desc' = 'asc') =>
	(a: T, b: T) => {
		const inverse = order === 'asc' ? 1 : -1;

		if (a[property] === b[property]) {
			return 0;
		}

		return (a[property] > b[property] ? 1 : -1) * inverse;
	};

export const orderOnKey =
	<T extends string, U = any>({ key, order }: { key: T; order: U[] }) =>
	<O extends Record<T, U>>(objects: O[]): O[] => {
		const { ordered, remaining } = order.reduce(
			({ ordered, remaining }, keyValue) => {
				const index = remaining.findIndex((item) => item[key] === keyValue);

				if (index === -1) {
					return { ordered, remaining };
				}

				const item = remaining.splice(index, 1);

				return {
					ordered: ordered.concat(item),
					remaining,
				};
			},
			{ ordered: [] as O[], remaining: [...objects] }
		);

		return ordered.concat(remaining);
	};
